import React, { useEffect, useState, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { get, capitalize } from 'lodash'
import { Formik, Form } from 'formik'
import FormikOnChange from 'components/form/FormikAutoSave'
import { FormikDropdownField } from 'components/form/Dropdown'
import { Grid } from '@material-ui/core'
import AdminCommonListLayout from '../layout/AdminCommonListLayout'
import { adminBeneficiaryVettingDemographicList } from './actions/admin_beneficiary_vetting_demographic'
import { adminBeneficiaryVettingList } from './actions/admin_beneficiary_vetting'
import { adminAgeGroups, adminSexes, adminRaces } from 'admin/actions/admin_dropdown_options'
import AdminBeneficiaryVettingDemographic from './AdminBeneficiaryVettingDemographic'
import VettingDemographicsTotal from './VettingDemographicsTotal'
import { Modal } from 'components/layout/Modal'
import { Separator } from 'components/layout/Separator'

const addAllOption = (list) => {
  const allOption = {
    label: 'All',
    value: 'all',
    original_item: { id: 'all', name: 'All' }
  }
  return [...(list || []), allOption]
}

export default function AdminBeneficiaryVettingDemographics({ beneficiary_vetting_id, inTabs = false }) {
  const dispatch = useDispatch()
  const prevVettingIdRef = useRef(undefined)
  const vetting = useSelector(() => adminBeneficiaryVettingList.getObject(beneficiary_vetting_id))
  const vetting_status = get(vetting, 'status')
  const actionsDisabled = vetting_status !== 'vetting_in_progress'
  const demographics = useSelector(() => adminBeneficiaryVettingDemographicList.getVisibleObjects())
  const is_loading = useSelector(() => adminBeneficiaryVettingDemographicList.isLoading())
  const as_table = inTabs
  const initial_filter = beneficiary_vetting_id
    ? { beneficiary_vetting: beneficiary_vetting_id }
    : {}
  const [filter, setFilter] = useState(initial_filter)
  const [openModal, setOpenModal] = useState(false)
  const [editingDemographicId, setEditingDemographicId] = useState(null)

  useEffect(() => {
    if (!beneficiary_vetting_id) return
    if (prevVettingIdRef.current !== beneficiary_vetting_id) {
      prevVettingIdRef.current = beneficiary_vetting_id
      dispatch(adminBeneficiaryVettingDemographicList.invalidateList())
      setFilter({ beneficiary_vetting: beneficiary_vetting_id })
    }
    const formatted = { beneficiary_vetting: beneficiary_vetting_id }
    if (filter.race && filter.race !== 'all') formatted.race = filter.race
    if (filter.sex && filter.sex !== 'all') formatted.sex = filter.sex
    if (filter.age_group && filter.age_group !== 'all') formatted.age_group = filter.age_group
    dispatch(adminBeneficiaryVettingDemographicList.updateListFilter(formatted))
    dispatch(adminBeneficiaryVettingDemographicList.fetchListIfNeeded())
  }, [dispatch, beneficiary_vetting_id, filter])

  useEffect(() => {
    dispatch(adminAgeGroups.fetchListIfNeeded())
    dispatch(adminSexes.fetchListIfNeeded())
    dispatch(adminRaces.fetchListIfNeeded())
  }, [dispatch])

  const closeEditModal = () => {
    setEditingDemographicId(null)
    dispatch(adminBeneficiaryVettingDemographicList.invalidateList())
    dispatch(adminBeneficiaryVettingDemographicList.fetchListIfNeeded())
    setOpenModal(false)
  }

  const onEditDemographic = (demographic_id) => {
    setEditingDemographicId(demographic_id)
    setOpenModal(true)
  }

  const onAddDemographic = () => {
    setEditingDemographicId(null)
    setOpenModal(true)
  }

  const onFilterChanged = (values) => {
    const formatted = { ...values }
    Object.keys(formatted).forEach((key) => {
      if (formatted[key] === 'all' || formatted[key] === '' || formatted[key] == null) {
        delete formatted[key]
      }
    })
    if (beneficiary_vetting_id) formatted.beneficiary_vetting = beneficiary_vetting_id
    setFilter(formatted)
  }

  const renderFilter = () => {
    if (openModal) return null
    const race_options = addAllOption(adminRaces.getAsOptions())
    const sex_options = addAllOption(adminSexes.getAsOptions())
    const age_group_options = addAllOption(adminAgeGroups.getAsOptions())
    return (
      <Formik
        initialValues={{
          beneficiary_vetting: beneficiary_vetting_id,
          race: filter.race || 'all',
          sex: filter.sex || 'all',
          age_group: filter.age_group || 'all'
        }}
        onSubmit={onFilterChanged}
        enableReinitialize={true}
      >
        {(formik_props) => (
          <Form>
            <FormikOnChange onChange={(values) => onFilterChanged(values)} />
            <Grid container spacing={1}>
              <Grid item xs={12} sm={3}>
                <FormikDropdownField
                  name="race"
                  label="Race"
                  formik_props={formik_props}
                  options={race_options || []}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormikDropdownField
                  name="sex"
                  label="Sex"
                  formik_props={formik_props}
                  options={sex_options || []}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <FormikDropdownField
                  name="age_group"
                  label="Age Group"
                  formik_props={formik_props}
                  options={age_group_options || []}
                />
              </Grid>
            </Grid>
          </Form>
        )}
      </Formik>
    )
  }

  const columns = [
    {
      field: 'race',
      title: 'Race',
      render: (item) => capitalize(get(item, 'race', ''))
    },
    {
      field: 'sex',
      title: 'Sex',
      render: (item) => capitalize(get(item, 'sex', ''))
    },
    {
      field: 'age_group',
      title: 'Age Group',
      render: (item) => capitalize(get(item, 'age_group', ''))
    },
    {
      field: 'number_of_people',
      title: 'Number of People'
    }
  ]

  if (!beneficiary_vetting_id) {
    return null
  }

  return (
    <>
      <AdminCommonListLayout
        add_label="Add Demographic"
        title="Demographics"
        onAddRow={actionsDisabled ? undefined : onAddDemographic}
        onEditRow={actionsDisabled ? undefined : onEditDemographic}
        canDelete={!actionsDisabled}
        is_loading={is_loading}
        columns={columns}
        item_list={adminBeneficiaryVettingDemographicList}
        as_table={as_table}
        enableAnyFieldFilter
        renderAdditionalFilter={renderFilter}
        extra_content_top={
          <VettingDemographicsTotal data={demographics} is_loading={is_loading} filters={filter} />
        }
      >
        {openModal && (
          <Modal title="Add/Edit Demographic" onClose={closeEditModal}>
            <Separator height={15} />
            <AdminBeneficiaryVettingDemographic
              in_modal={true}
              onClose={closeEditModal}
              passed_demographic_id={editingDemographicId}
              beneficiary_vetting_id={beneficiary_vetting_id}
            />
          </Modal>
        )}
      </AdminCommonListLayout>
    </>
  )
}
